import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send, CheckCircle2 } from 'lucide-react';
import { siteInfo, winterHours } from '../data/mock';
import { useToast } from '../hooks/use-toast';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    try {
      const messages = JSON.parse(localStorage.getItem('lss_messages') || '[]');
      messages.push({ ...form, ts: new Date().toISOString() });
      localStorage.setItem('lss_messages', JSON.stringify(messages));
    } catch {
      // localStorage may be unavailable (private mode / quota); non-critical here.
    }
    toast({ title: 'Message sent!', description: 'We\'ll get back to you within 24 hours.' });
    setSubmitted(true);
  };

  return (
    <main>
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Get In Touch</div>
            <h1 className="font-display font-bold text-4xl md:text-6xl text-splash-green leading-tight">Let’s chat about your child’s next big splash.</h1>
            <p className="mt-6 text-splash-green/80 leading-relaxed">
              Have a question about lessons, scheduling, or your child’s comfort level? Reach out and Coach Abrie or a member of our team will follow up personally.
            </p>
            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-splash-teal/15 text-splash-teal grid place-items-center"><MapPin className="w-5 h-5" /></div>
                <div><div className="text-xs uppercase tracking-widest text-splash-green/60">Primary Location</div><div className="text-splash-green font-semibold">{siteInfo.address}</div></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-splash-teal/15 text-splash-teal grid place-items-center"><Mail className="w-5 h-5" /></div>
                <div><div className="text-xs uppercase tracking-widest text-splash-green/60">Email</div><a href={`mailto:${siteInfo.email}`} className="text-splash-green font-semibold hover:text-splash-teal">{siteInfo.email}</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-splash-teal/15 text-splash-teal grid place-items-center"><Phone className="w-5 h-5" /></div>
                <div><div className="text-xs uppercase tracking-widest text-splash-green/60">Text or Call</div><a href={`tel:${siteInfo.phone}`} className="text-splash-green font-semibold hover:text-splash-teal">{siteInfo.phone}</a></div>
              </div>
            </div>
            <div className="mt-10 rounded-2xl bg-splash-cream border border-splash-sand p-5">
              <div className="font-display font-bold text-xl text-splash-green mb-2">Winter 2026&ndash;2027 Lesson Hours</div>
              <ul className="text-splash-green/80 text-sm space-y-1" data-testid="contact-hours">
                {winterHours.map(([d, h]) => (
                  <li key={d}><strong className="text-splash-green">{d}</strong> &middot; {h}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-splash-sand p-8 shadow-sm">
            {submitted ? (
              <div className="py-16 text-center">
                <CheckCircle2 className="w-14 h-14 text-splash-teal mx-auto mb-4" />
                <h3 className="font-display font-bold text-2xl text-splash-green">Message received!</h3>
                <p className="mt-2 text-splash-green/75">Thank you for reaching out. We’ll respond within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <h3 className="font-display font-bold text-2xl text-splash-green">Send us a message</h3>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-splash-green/70 mb-2">Your Name</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-splash-sand bg-splash-cream/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-splash-teal" placeholder="Jane Parent" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-splash-green/70 mb-2">Email</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-splash-sand bg-splash-cream/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-splash-teal" placeholder="you@email.com" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-splash-green/70 mb-2">Message</label>
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-splash-sand bg-splash-cream/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-splash-teal" placeholder="Tell us about your child and what you're looking for..." />
                </div>
                <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-6 py-3 font-semibold btn-transition">Send Message <Send className="w-4 h-4" /></button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
